import { Prop, Component, Vue, Watch } from 'vue-facing-decorator'

type LoadState = 'loading' | 'loaded' | 'error'

/**
 * Wraps an <img> with two non-textual states:
 *   - `loading` → animated shimmer skeleton
 *   - `error`   → empty contrasting box (no text or icons) when the asset fails
 *   - `loaded`  → the image itself
 *
 * Sizing and border-radius are inherited from the `className` applied to the
 * wrapper, so each consumer keeps controlling the slot dimensions.
 */
@Component
export default class SkeletonImageComponent extends Vue {
  /** Image URL to display. */
  @Prop({ required: true }) src!: string
  /** Accessible alt text forwarded to the underlying <img>. */
  @Prop alt: string | undefined = ''
  /** Class applied to the wrapper — sizing/border-radius come from here. */
  @Prop className: string | undefined = ''

  state: LoadState = 'loading'

  mounted(): void {
    // A cached image can finish loading before the @load listener is attached,
    // so reconcile against the element's actual state on mount.
    const img = this.$refs.img as HTMLImageElement | undefined
    if (img?.complete) {
      this.state = img.naturalWidth > 0 ? 'loaded' : 'error'
    }
  }

  @Watch('src')
  onSrcChange(): void {
    this.state = 'loading'
  }

  onLoad(): void {
    this.state = 'loaded'
  }

  onError(): void {
    this.state = 'error'
  }
}
